import classNames from 'classnames';
import classnames from 'classnames';
import { FormattedMessage } from 'components/shared/Intl';
import { FC, ReactNode, useMemo } from 'react';
import styled from 'styled-components';

export type CardColors =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'white'
  | 'filling-primary'
  | 'filling-secondary'
  | 'filling-success'
  | 'filling-warning'
  | 'filling-danger'
  | 'filling-info';

export interface AlertBannerProps {
  className?: string;
  color?: CardColors;

  btnColor?: 'primary' | 'primary-alt' | 'info';
  dismissible?: boolean;
  e2e?: string;
  icon?: string;
  messages?: string[];
  onDismissed?: () => void;
  spin?: boolean;
  children?: ReactNode;
}

export const StyledDiv = styled.div`
  & > p:last-child {
    margin-bottom: 0;
  }
`;

export const StyledAlertBannerContainer = styled.div`
  z-index: 1;
`;

export const AlertBanner: FC<AlertBannerProps> = ({
  className,
  color = 'info',
  btnColor = 'primary-alt',
  dismissible = false,
  e2e,
  icon = 'info_outline',
  messages,
  onDismissed,
  spin = false,
  children,
}) => {
  const containerClasses = useMemo(() => classnames('alert-banner-component', 'bg-lvl1', className), [className]);
  const cardClasses = useMemo(() => classnames('card', getCardColorClass(color), 'w-100', 'h-100'), [color]);
  const iconClasses = useMemo(
    () => classnames('icon icon-md me-2 align-self-start', spin ? 'icon-spin' : undefined),
    [spin],
  );

  return (
    <StyledAlertBannerContainer className={containerClasses} data-e2e={e2e}>
      <div className={cardClasses}>
        <div className="card-body">
          <div className="card-text d-flex align-items-center">
            <i className={iconClasses}>{icon}</i>

            {messages && (
              <StyledDiv className="flex-grow-1 text-justify">
                {messages.map((message, index) => (
                  <div key={index}>
                    <FormattedMessage id={message as any} defaultMessage={message} />
                  </div>
                ))}
              </StyledDiv>
            )}

            {children && <StyledDiv className="flex-grow-1">{children}</StyledDiv>}

            {dismissible && (
              <button
                type="button"
                className={classNames('btn btn-sm', `btn-flat-${btnColor}`, 'ms-1 align-self-start')}
                aria-label="Close"
                onClick={onDismissed}
                data-e2e={e2e ? `${e2e}-close` : undefined}
              >
                <i className="icon">clear</i>
              </button>
            )}
          </div>
        </div>
      </div>
    </StyledAlertBannerContainer>
  );
};

function getCardColorClass(color: CardColors): string {
  return color.includes('filling') ? `card-${color} active` : `bg-${color} text-primary-alt`;
}
